import {joiResolver} from '@hookform/resolvers/joi';
import {AxiosError} from 'axios';
import React, {useCallback, useMemo, useState} from 'react';
import {useForm, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {MutationStatus} from 'react-query';
import {useHistory, useLocation} from 'react-router-dom';

import {Box, MUIDataTableColumnDef, Typography} from 'web-library';
import {candidateService} from '../../../api';
import {useDeleteOpsAdmin} from '../../../api-refactored/user/useDeleteOpsAdmin';
import {
  EditOpsAdminRequest,
  usePutEditOpsAdmin,
} from '../../../api-refactored/user/usePutEditOpsAdmin';
import {UseCustomMutationReturn} from '../../../api-refactored/utils/useCustomMutation';
import {EditRequestData} from '../../../models';
import editOpsAdminSchema from '../../../models/@admin/editOpsAdminSchema';
import {ViewSelectedSubjectPapers} from '../../../api/candidate/exam/model';
import {ReviewSubjectsResponse} from '../../../api/candidate/subject/model';
import {GenericErrorResponse} from "../../../api-refactored/typings";

const subjectFlowRouteKey = {
  editSubject: 'edit-subject',
  addSubject: 'add-subject',
  reviewSubject: 'review-subject',
  reviewParticular: 'review-particular',
  submitAmendment: 'submit-amendment',
};

/**
 * A hook to provide subject amendment flow
 * @param url Current url of the page
 * @returns
 */
export const useSubjectAmendmentRoute = (url: string) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const {state: locationState} = useLocation<any>();

  const toStep1 = useCallback(() => {
    setStep(0);
    if (locationState) {
      history.push(`${url}/${locationState.uuid}/${subjectFlowRouteKey.editSubject}`);
    }
  }, [history, url, locationState]);

  const toStep2 = useCallback(() => {
    setStep(1);
    if (locationState) {
      history.push(`${url}/${locationState.uuid}/${subjectFlowRouteKey.addSubject}`);
    }
  }, [history, url, locationState]);

  const toStep3 = useCallback(() => {
    setStep(2);
    history.push(`${url}/${subjectFlowRouteKey.reviewSubject}`);
  }, [history, url]);

  const toStep4 = useCallback(() => {
    setStep(3);
    history.push(`${url}/${subjectFlowRouteKey.reviewParticular}`);
  }, [history, url]);

  const toStep5 = useCallback(() => {
    setStep(4);
    history.push(`${url}/${subjectFlowRouteKey.submitAmendment}`);
  }, [history, url]);

  return {
    step,
    localRouteKey: subjectFlowRouteKey,
    toStep1,
    toStep2,
    toStep3,
    toStep4,
    toStep5,
  };
};

export interface EditDeleteReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  checkSubmit: (submitData: any) => void;
  /**
   * Form to send edit & delete
   */
  form: UseFormReturn<Record<string, any>>;
  /**
   * React query mutation object
   */
  editMutation: UseCustomMutationReturn<void, AxiosError<any>, EditOpsAdminRequest, unknown>;
  deleteMutation: UseCustomMutationReturn<void, AxiosError<any>, void, unknown>;
  /**
   * Extra handling error message for the create candidate api
   */
  submitError: string;
  submitDelete: () => any;
}

/**
 * A hook to provide utils for detail flow step 2
 * @param selectedUser Target user to perform the block/unblock request
 * @param next Function to the next step in block flow
 * @returns
 */
export const useEditDeleteFunction = (
    selectedUser: number | null,
    next: () => void,
    pending: boolean | null,
    editingMode: boolean
): EditDeleteReturn => {
  const form = useForm({
    mode: 'onBlur',
    resolver: joiResolver(editOpsAdminSchema),
  });

  const [submitError, setSubmitError] = useState('');

  const editMutation = usePutEditOpsAdmin({
    id: selectedUser ?? -1,
    onSuccess: () => {
      next();
    },
    onError: (err: AxiosError<GenericErrorResponse>) => {
      const applicationCode: string = err.response?.data.code!;
      switch (err.response?.status) {
        case undefined:
          setSubmitError('');
          break;
        case 400:
          if (applicationCode === '1202') {
            setSubmitError('User with the same email already exists');
          } else {
            setSubmitError('Bad Request');
          }
          break;
        default:
          setSubmitError('Unknown error. Please try again.');
          break;
      }
    },
  });

  const deleteMutation = useDeleteOpsAdmin({
    id: selectedUser ?? -1,
    onSuccess: () => {
      next();
    },
  });

  const submitEdit = useCallback(
      (submitData: EditRequestData) => {
        if (selectedUser) {
          editMutation.mutate({
            email: submitData.email,
            roles: submitData.roles.map((item) => item.id),
            approverRole: submitData.approver,
          });
        }
      },
      [editMutation, selectedUser]
  );

  const submitDelete = useCallback(() => {
    if (selectedUser && !pending) {
      deleteMutation.mutate();
    }
  }, [deleteMutation, pending, selectedUser]);

  const checkSubmit = useCallback(
      (submitData: EditRequestData) => {
        if (editingMode) {
          submitEdit(submitData);
        } else {
          submitDelete();
        }
      },
      [submitDelete, submitEdit, editingMode]
  );

  return {
    form,
    checkSubmit,
    submitError,
    editMutation,
    deleteMutation,
    submitDelete,
  };
};

/**
 * A hook to handle all user type filter logic, mainly 'Candidate' and 'Coordinator'
 * @returns
 */
export const useUserTypeFilter = () => {
  const userTypesData = [
    {
      label: 'Candidate',
      value: 'candidate',
    },
    {
      label: 'Coordinator',
      value: 'centre',
    },
  ];
  const [userType, setUserType] = useState('');
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserType((event.target as HTMLInputElement).value);
  };

  return {
    userTypesData,
    userType,
    handleRadioChange,
  };
};

/**
 * A hook to help decide candidate user index & centre user index according to account type selected, not matching account type will return -1 index
 * @param selectedUser current selected user
 * @returns
 */
export const useUserIndexSplit = (selectedUser: number | null) => {
  const opsAdminIndex = useMemo(() => selectedUser || -1, [selectedUser]);

  return {
    opsAdminIndex,
  };
};

export const usePostSubjectValidation = (path: string) => {
  const {t} = useTranslation();
  const history = useHistory();
  const [apiStatus, setApiStatus] = useState<MutationStatus>('idle');

  const [subjectValidationMsg, setSubjectValidationMsg] = useState<string[]>([]);
  const postSubjectValidation = candidateService.exam.candidate.usePostSubjectValidation(
      (res) => {
        const errorMsg = [...res.data];

        if (errorMsg.length) {
          setSubjectValidationMsg(errorMsg);
          setApiStatus('error');
        } else {
          setApiStatus('success');
          history.push(path);
        }
      },
      () => {
        setApiStatus('error');
        setSubjectValidationMsg([t('exam->>SelectSubjects.SubjectValidationUnknownError')]);
      }
  );

  return {subjectValidationMsg, postSubjectValidation, apiStatus, setApiStatus};
};

export interface Translations {
  subjectName: string;
  subjectCode: string;
  subjectLevel: string;
  examSeries: string;
  examLevel: string;
  languageMedium: string;
  actionTaken: string;
}

export const useReviewSubjectColumn = (
    classes: Record<string,string>,
    translations: Translations,
    data?: ReviewSubjectsResponse
): MUIDataTableColumnDef[] => {
  const {t} = useTranslation();

  return [
    {
      name: 'subjectName',
      label: translations.subjectName,
      options: {
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => {
          const optionalPaperList: string[] = [];
          if (data?.registeredSubjects) {
            data?.registeredSubjects[
                dataIndex
                ].optionalPapers.forEach((item: ViewSelectedSubjectPapers) =>
                optionalPaperList.push(
                    `${t('amendment->>ViewSelectedSubjects.Paper')} ${item.paperNo}`
                )
            );
          }

          return (
              <Box className={classes.subjectContainer}>
                {data?.registeredSubjects && (
                    <Typography className={classes.subjectText}>
                      {data?.registeredSubjects[dataIndex].subjectName} (
                      {data?.registeredSubjects[dataIndex].subjectCode})
                    </Typography>
                )}

                {optionalPaperList.length ? (
                    <Typography className={classes.optionalPapers}>
                      {t('amendment->>ViewSelectedSubjects.OptionalPapers')}:{' '}
                      {optionalPaperList.join(', ')}
                    </Typography>
                ) : null}
              </Box>
          );
        },
      },
    },
    {
      name: 'subjectLevel',
      label: translations.subjectLevel,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'examSeries',
      label: translations.examSeries,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({style: {minWidth: '130px', maxWidth: '130px'}}),
      },
    },
    {
      name: 'examLevel',
      label: translations.examLevel,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({style: {minWidth: '180px', maxWidth: '180px'}}),
      },
    },
    {
      name: 'language',
      label: translations.languageMedium,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'actionTaken',
      label: translations.actionTaken,
      options: {
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => (
            <Typography variant="h6" className={classes.actionTaken}>
              {data?.registeredSubjects[dataIndex].actionTaken}
            </Typography>
        ),
      },
    },
  ];
};

export const useReviewSubjectDeleteColumn = (
    classes: Record<string,string>,
    translations: Translations,
    data?: ReviewSubjectsResponse
): MUIDataTableColumnDef[] => {
  const {t} = useTranslation();

  return [
    {
      name: 'subjectName',
      label: translations.subjectName,
      options: {
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => {
          const optionalPaper: string[] = [];
          if (data?.deletedSubjects) {
            data?.deletedSubjects[
                dataIndex
                ].optionalPapers.forEach((item: ViewSelectedSubjectPapers) =>
                optionalPaper.push(`${t('amendment->>ViewSelectedSubjects.Paper')} ${item.paperNo}`)
            );
          }

          return (
              <Box className={classes.subjectContainer}>
                {data?.deletedSubjects && (
                    <Typography className={classes.subjectText}>
                      {data?.deletedSubjects[dataIndex].subjectName} (
                      {data?.deletedSubjects[dataIndex].subjectCode})
                    </Typography>
                )}

                {optionalPaper.length ? (
                    <Typography className={classes.optionalPapers}>
                      {t('amendment->>ViewSelectedSubjects.OptionalPapers')}: {optionalPaper.join(', ')}
                    </Typography>
                ) : null}
              </Box>
          );
        },
      },
    },
    {
      name: 'subjectLevel',
      label: translations.subjectLevel,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'examSeries',
      label: translations.examSeries,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({style: {minWidth: '130px', maxWidth: '130px'}}),
      },
    },
    {
      name: 'examLevel',
      label: translations.examLevel,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({style: {minWidth: '180px', maxWidth: '180px'}}),
      },
    },
    {
      name: 'language',
      label: translations.languageMedium,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'actionTaken',
      label: translations.actionTaken,
      options: {
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => (
            <Typography variant="h6" className={classes.actionTaken}>
              {data?.deletedSubjects[dataIndex].actionTaken}
            </Typography>
        ),
      },
    },
  ];
};
